'use client';

import { c as _c } from "react/compiler-runtime";
import { useAttributeXWaitlist, useAuth } from '@morfi/backend-sdk-v2/client';
import { useEffect, useRef } from 'react';
import { getStoredReferralCode } from '@/components/early-access-gate/early-access-referral';
import { logger } from '@/lib/telemetry/logger';
import { useLocaleStore } from '@/stores/locale';

/**
 * Set by the gate's "Continue with X" (and the rewards-gate connect-X) BEFORE
 * the Turnkey X OAuth. Signals that, once auth resolves, this X user must be
 * attributed to the waitlist server-side — the prod `upsertXSignup` row — so an
 * account-creating X signup is still counted + referral-attributed exactly like
 * the legacy lightweight X waitlist join.
 */
const PENDING_KEY = 'morfi_waitlist_x_attribute_pending';

/** Mark that the next successful X auth should attribute the waitlist.
 *  localStorage (not sessionStorage): the OAuth redirect can land in a fresh
 *  tab — the flag must survive so the referral attribution isn't lost. The
 *  row itself is server-guaranteed either way; this call is what carries the
 *  partner referral code. A stale flag is harmless: the hook is idempotent
 *  and only fires for accounts with a connected X provider. */
export function markWaitlistXAttributePending(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_KEY, '1');
  } catch (err: unknown) {
    // SILENT-CATCH: storage blocked (privacy mode) — attribution is
    // best-effort; the account still gets created.
    logger.warn('waitlist-x attribute: pending flag write failed', {
      error: err instanceof Error ? err.message.slice(0, 120) : String(err)
    });
  }
}

/** Clear the pending flag (X OAuth cancelled/failed, or after attribution). */
export function clearWaitlistXAttributePending(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_KEY);
    // Historic location — clear both so pre-migration flags can't linger.
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // SILENT-CATCH: nothing actionable if removal throws.
  }
}
function isPending(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PENDING_KEY) === '1' || window.sessionStorage.getItem(PENDING_KEY) === '1';
  } catch {
    // SILENT-CATCH: storage can throw (privacy mode / disabled storage);
    // treat as "not pending" — the safe default.
    return false;
  }
}

/**
 * One-shot, post-auth waitlist attribution for an X signup.
 *
 * Mounted once in `IdentityProvider`, it fires as a side effect when auth
 * resolves and the pending flag is set. The
 * Turnkey X OAuth creates the account client-side; this writes the matching
 * waitlist row (with referral) via the SDK so we never lose the signup
 * attribution. Survives the OAuth full-page redirect because the flag lives in
 * sessionStorage and the referral code in its cookie.
 */
export function useAttributeWaitlistXSignup() {
  const $ = _c(5);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const attribute = useAttributeXWaitlist();
  const firedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== attribute || $[1] !== isAuthenticated || $[2] !== user) {
    t0 = () => {
      if (firedRef.current || !isAuthenticated || user === null || !isPending()) {
        return;
      }
      const hasX = user.connectedProviders?.some(_temp) ?? false;
      if (!hasX) {
        return;
      }
      firedRef.current = true;
      const locale = useLocaleStore.getState().locale;
      const referralCode = getStoredReferralCode() ?? undefined;
      attribute.mutate(referralCode !== undefined ? {
        referralCode,
        locale
      } : {
        locale
      }, {
        onSuccess: _temp2,
        onError: err => {
          firedRef.current = false;
          logger.warn("waitlist-x attribute failed", {
            error: err.message.slice(0, 160)
          });
        }
      });
    };
    t1 = [isAuthenticated, user, attribute];
    $[0] = attribute;
    $[1] = isAuthenticated;
    $[2] = user;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function _temp2() {
  return clearWaitlistXAttributePending();
}
function _temp(p) {
  return p.provider === "x";
}